










































































import Vue from 'vue'
import { mapGetters } from 'vuex'

import ClickOutside from 'vue-click-outside'

import { GoogleMapService } from '@/services'
import CityQuickLinks from '@/components/CityQuickLinks.vue'

export default Vue.extend({
  name: 'SurveySearchField',

  components: {
    CityQuickLinks,
  },

  directives: {
    ClickOutside,
  },

  props: {
    placeholder: {
      type: String,
      default: 'Start typing',
    },
    initialValue: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      searchExpanded: false,
      debounceTimeout: null,
      searchTextDebounced: '',
      placesService: null,
      autocompleteError: false,
      autocompleteService: null,
      autocompleteSessionToken: null,
      autocompleteResults: [],
      autocompleteResultsLoading: false,
      googleLibraryLoaded: false,
      geolocating: false,
      geolocation: {
        loc: null,
        geocoder: null,
        position: null,
      },
    }
  },

  computed: {
    ...mapGetters('offers', ['searchLatitude', 'searchLongitude']),
    autocompleteText: {
      get() {
        return this.searchTextDebounced
      },
      set(newValue: string) {
        if (this.debounceTimeout) clearTimeout(this.debounceTimeout)

        if (newValue.length === 0 || !this.searchExpanded) {
          this.searchTextDebounced = newValue
          return
        }

        this.debounceTimeout = setTimeout(() => {
          this.searchTextDebounced = newValue
        }, 500)
      },
    },
  },

  watch: {
    searchTextDebounced() {
      this.search()
    },
    initialValue() {
      this.searchTextDebounced = this.initialValue
    },
  },

  mounted: function () {
    GoogleMapService.init()
      .then(() => {
        this.autocompleteService = new window.google.maps.places.AutocompleteService()
        this.placesService = new window.google.maps.places.PlacesService(document.createElement('div'))

        if (this.initialValue) {
          this.searchTextDebounced = this.initialValue

          if (this.searchLatitude && this.searchLongitude) {
            this.search(true)
            return
          }

          GoogleMapService.geocode(this.initialValue).then((result) => {
            if (result.error) {
              this.handleSearchFieldChanged(null, null, null)

              return
            }

            this.handleSearchFieldChanged(result.address, result.latitude, result.longitude)
            this.search(true)
          })
        }
      })
      .then(() => {
        Vue.nextTick(() => {
          this.googleLibraryLoaded = true
        })
      })
  },

  methods: {
    clear() {
      this.autocompleteText = ''
      this.handleSearchFieldChanged(null, null, null)
    },

    update(value: string) {
      this.autocompleteText = value
    },

    onClearSearchClicked() {
      if (this.autocompleteText.length === 0) {
        this.closeSearch()
        return
      }

      this.clear()
    },

    closeSearch() {
      this.searchExpanded = false
    },

    expandSearch() {
      this.searchExpanded = true
    },

    onKeyPress(event) {
      this.$emit('keypress', event)
    },

    onCityQuickLinkClicked(quickLink) {
      const address = `${quickLink.city}, ${quickLink.state}`
      this.handleSearchFieldChanged(address, quickLink.latitude, quickLink.longitude)

      this.closeSearch()
      this.update(address)
    },

    onWebOffersSearchResultSelected() {
      this.clear()
      this.closeSearch()
    },

    onSearchResultSelected(placeId) {
      this.placesService.getDetails(
        {
          placeId,
          fields: ['geometry', 'formatted_address'],
          sessionToken: this.sessionToken,
        },
        (result, status) => {
          this.handleSearchFieldChanged(result.formatted_address, result.geometry.location.lat(), result.geometry.location.lng())
          this.closeSearch()
          this.update(result.formatted_address)
        }
      )

      this.sessionToken = null
    },

    geolocate() {
      if (this.geolocating) return

      if (!navigator.geolocation) {
        this.$toast.error('Your browser does not support Geolocation')
        return
      }

      this.closeSearch()
      this.geolocating = true
      navigator.geolocation.getCurrentPosition(this.onGeoLocationSuccess, this.onGeolocateError, { maximumAge: 600000, timeout: 30000 })
    },

    onGeoLocationSuccess(result) {
      const { latitude, longitude } = result.coords

      GoogleMapService.reverseGeocode(new window.google.maps.LatLng(latitude, longitude)).then((result) => {
        if (result.error) {
          this.$toast.error('Uh-oh, we had trouble finding your location. Please try again.')

          return
        }

        this.handleSearchFieldChanged(result.address, result.latitude, result.longitude)

        this.update(result.address)
        this.geolocating = false
      })
    },

    onGeolocateError(error) {
      this.geolocating = false

      switch (error.code) {
        case 1:
          this.$toast.warning('Please allow access to your location')
          break

        case 2:
          this.$toast.error('Location not available')
          break

        default:
          this.$toast.error('Unexpected error, please try again')
      }
    },

    setSearchResults(results, status) {
      this.autocompleteResultsLoading = false

      switch (status) {
        case window.google.maps.places.PlacesServiceStatus.OK:
          this.autocompleteResults = results.map((result) => {
            return { mainText: result.structured_formatting.main_text, secondaryText: result.structured_formatting.secondary_text, placeId: result.place_id }
          })
          break

        case window.google.maps.places.PlacesServiceStatus.NOT_FOUND:
        case window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS:
          this.autocompleteResults = []
          break

        default:
          this.autocompleteError = true
      }
    },

    search(force = false) {
      if (!force && !this.googleLibraryLoaded) return

      this.autocompleteError = false

      if (this.searchTextDebounced.length < 3) {
        this.autocompleteResults = []
        return
      }

      if (!this.autocompleteSessionToken) {
        this.autocompleteSessionToken = new window.google.maps.places.AutocompleteSessionToken()
      }

      this.autocompleteResultsLoading = true

      this.autocompleteService.getPlacePredictions(
        {
          input: this.searchTextDebounced,
          sessionToken: this.autocompleteSessionToken,
          componentRestrictions: { country: 'us' },
        },
        this.setSearchResults
      )
    },

    handleSearchFieldChanged(address, latitude, longitude) {
      this.$emit('survey-search-field-changed', {
        address,
        latitude: latitude,
        longitude: longitude,
      })
    },
  },
})
